import { t as resilientLazy } from "./lazy-implementation-8ID6ez-9.js";
import { jsx, jsxs } from "react/jsx-runtime";
import clsx from "clsx";
import { Suspense } from "react";
import css from "./markdown/CodeBlock.module.css";
import { fromMarkdown } from "mdast-util-from-markdown";
import { gfmFromMarkdown } from "mdast-util-gfm";
import { gfm } from "micromark-extension-gfm";
import { attention } from "micromark-core-commonmark";
import { unicodePunctuation } from "micromark-util-character";
import { classifyCharacter } from "micromark-util-classify-character";
import { codes, constants } from "micromark-util-symbol";
//#region lib/types/markdown/CodeBlock.js
/** Lightweight code-surface facade with a readable raw-text fallback. */
function PlainCodeBlock({ code, lang, className, busy = false }) {
	const trimmed = code.endsWith("\n") ? code.slice(0, -1) : code;
	return jsxs("div", {
		className: clsx(css.block, "md-code-block", className),
		"aria-busy": busy || void 0,
		children: [jsx("div", {
			className: css.bannerWrap,
			children: jsx("div", {
				className: css.banner,
				children: jsx("div", {
					className: css.infostring,
					children: lang ?? ""
				})
			})
		}), jsx("pre", {
			className: css.plain,
			children: jsx("code", { children: trimmed })
		})]
	});
}
function CodeBlockUnavailable(props) {
	return jsx("div", {
		"data-presentation-unavailable": "",
		children: jsx(PlainCodeBlock, { ...props })
	});
}
const LazyCodeBlock = resilientLazy(() => import("./CodeBlockImpl-XqDTmVXp.js").then((module) => module.CodeBlockImpl), CodeBlockUnavailable);
/**
* Render a code surface without placing copy behavior or syntax highlighting
* on the initial shell path. Pending and failed implementation loads retain
* selectable raw monospace text.
* @param props - Source text, language hint, placement class, and copy labels.
* @returns The raw facade or loaded interactive code block.
*/
function CodeBlock(props) {
	return jsx(Suspense, {
		fallback: jsx(PlainCodeBlock, {
			...props,
			busy: true
		}),
		children: jsx(LazyCodeBlock, { ...props })
	});
}
//#endregion
//#region lib/types/markdown/cjkFriendlyStrong.js
/** Let asterisk strong emphasis close after punctuation when CJK prose continues without whitespace. */
const cjkCharacter = new RegExp([
	"\\p{Script_Extensions=Han}",
	"\\p{Script_Extensions=Hiragana}",
	"\\p{Script_Extensions=Katakana}",
	"\\p{Script_Extensions=Hangul}",
	"\\p{Script_Extensions=Bopomofo}"
].join("|"), "u");
function isCjkCharacter(code) {
	return code !== null && code >= 0 && cjkCharacter.test(String.fromCodePoint(code));
}
const tokenizeCjkFriendlyAttention = function(effects, ok, nok) {
	const configuredAttentionMarkers = this.parser.constructs.attentionMarkers.null;
	if (configuredAttentionMarkers === void 0) throw new Error("micromark CommonMark attention markers are unavailable");
	const attentionMarkers = configuredAttentionMarkers;
	const previous = this.previous;
	const before = classifyCharacter(previous);
	let marker = codes.eof;
	return start;
	function start(code) {
		/* v8 ignore next -- this text construct is dispatched only for an asterisk. */
		if (code !== codes.asterisk) return nok(code);
		marker = code;
		effects.enter("attentionSequence");
		return inside(code);
	}
	function inside(code) {
		if (code === marker) {
			effects.consume(code);
			return inside;
		}
		const token = effects.exit("attentionSequence");
		const after = classifyCharacter(code);
		const open = !after || after === constants.characterGroupPunctuation && Boolean(before) || attentionMarkers.includes(code);
		const commonMarkClose = !before || before === constants.characterGroupPunctuation && Boolean(after) || attentionMarkers.includes(previous);
		const cjkStrongClose = token.end.offset - token.start.offset >= 2 && unicodePunctuation(previous) && isCjkCharacter(code);
		const close = commonMarkClose || cjkStrongClose;
		token._open = open;
		token._close = close;
		return ok(code);
	}
};
const cjkFriendlyAttention = {
	name: "cjkFriendlyAttention",
	resolveAll: attention.resolveAll,
	tokenize: tokenizeCjkFriendlyAttention
};
const cjkFriendlyStrongExtension = { text: { [codes.asterisk]: cjkFriendlyAttention } };
/**
* Extend CommonMark asterisk strong emphasis for punctuation-delimited CJK
* prose, as a micromark syntax extension for `fromMarkdown`.
* @returns The micromark syntax extension.
*/
function cjkFriendlyStrong() {
	return cjkFriendlyStrongExtension;
}
//#endregion
//#region lib/types/markdown/parse-gfm.js
/** GFM-only parser shared by streaming Markdown and synchronous plain-text extraction. */
/**
* Parse GFM without math extensions, so synchronous plain-text consumers do
* not bring the TeX grammar into the shell dependency graph.
* @param text - Markdown source.
* @returns The mdast root.
*/
function parseGfm(text) {
	return fromMarkdown(text, {
		extensions: [gfm(), cjkFriendlyStrong()],
		mdastExtensions: [gfmFromMarkdown()]
	});
}
//#endregion
export { cjkFriendlyStrong as n, CodeBlock as r, parseGfm as t };

//# sourceMappingURL=parse-gfm-Vs9Tf-4m.js.map